import { TreeNode } from 'primeng/api';

import { AppConstant } from './app.constant';

const ENGINEER_LABEL: string = AppConstant.ROLES.ENGINEER.label;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toText = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

/** Role names of the `user/list/role` response (`{ roles: { code: name } }`, `{ data: [...] }` or an array). */
function getRoleNames(response: unknown): string[] {
  const data = isRecord(response)
    ? (response['roles'] ?? response['data'] ?? response)
    : response;

  if (Array.isArray(data)) {
    return data
      .map(item => (isRecord(item) ? toText(item['name']) : toText(item)))
      .filter(Boolean);
  }
  if (isRecord(data)) {
    return Object.values(data).map(toText).filter(Boolean);
  }
  return [];
}

interface DisciplineItem {
  id: string;
  name: string;
}

/** Disciplines of the `discipline` response (`{ data: [...] }` or an array). */
function getDisciplines(response: unknown): DisciplineItem[] {
  const data = isRecord(response) ? response['data'] : response;
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .filter(isRecord)
    .map(item => ({
      id: toText(item['disciplineId'] ?? item['id']),
      name: toText(item['disciplineName'] ?? item['name']),
    }))
    .filter(item => item.name);
}

/**
 * Builds the role / discipline hierarchy used by Settings & Team Management:
 * every role is a node and Engineer has one child per discipline, labelled
 * `Engineer (<discipline>)`. The node label is the title text.
 * When an API returns nothing, the static `AppConstant.VRO_ROLES` / `AppConstant.DISCIPLINES`
 * (frontend mirror of the backend `remote-center.ts`) are used instead.
 */
export function buildRoleTitleTree(
  roleResponse: unknown,
  disciplineResponse: unknown
): TreeNode[] {
  const apiRoles = getRoleNames(roleResponse);
  const roleNames = apiRoles.length
    ? apiRoles
    : AppConstant.VRO_ROLES.map(role => role.label);
  const apiDisciplines = getDisciplines(disciplineResponse);
  const disciplines = apiDisciplines.length
    ? apiDisciplines
    : AppConstant.DISCIPLINES.map(discipline => ({ id: '', name: discipline.name }));

  return roleNames.map(name => {
    const node: TreeNode<string> = {
      key: `role-${name}`,
      label: name,
      data: name,
      leaf: true,
    };

    if (name === ENGINEER_LABEL && disciplines.length) {
      node.children = disciplines.map(discipline => {
        const label = `${ENGINEER_LABEL} (${discipline.name})`;
        return {
          key: `role-engineer-${discipline.id || discipline.name}`,
          label,
          data: label,
          leaf: true,
        };
      });
      node.leaf = false;
    }
    return node;
  });
}

/** The node with the given label at any depth, or null. */
export function findTitleNode(
  nodes: TreeNode[],
  label: string
): TreeNode | null {
  for (const node of nodes) {
    if (node.label === label) {
      return node;
    }
    const child = node.children ? findTitleNode(node.children, label) : null;
    if (child) {
      return child;
    }
  }
  return null;
}
